import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { api, API, errMsg } from "@/lib/api";
import { chapterLabel, fmtMinutes, fmtNum, readMinutes } from "@/lib/studio";
import { ReaderText } from "@/pages/ReaderPage";
import { FanBadge } from "@/components/fanworks/FanBadge";
import { RightsReportButton } from "@/components/fanworks/RightsReportButton";

const Cta = ({ sid, id }) => (
  <div className="flex flex-wrap gap-3">
    <Button asChild data-testid={`shared-signup-${id}`}><Link to={`/register?next=/read/${sid}`}>Sign up free to keep reading</Link></Button>
    <Button asChild variant="outline" data-testid={`shared-login-${id}`}><Link to={`/login?next=/read/${sid}`}>Log in</Link></Button>
  </div>
);

export default function SharedStoryPage() {
  const { sid } = useParams();
  const { user } = useAuth();
  const { data: s, error } = useQuery({ queryKey: ["public-story", sid], queryFn: () => api.get(`/public/stories/${sid}`).then((r) => r.data), retry: false, enabled: user !== null });
  if (error) return <div className="p-10 text-sm text-[#F87171]" data-testid="shared-error">{errMsg(error)}</div>;
  if (!s) return null;
  if (user) return <Navigate to={`/read/${s.id}`} replace />;
  const id = s.id;
  const ch = s.first_chapter;
  return (
    <div className="min-h-screen bg-[#0B0D10]" data-testid="shared-story-page">
      <header className="flex items-center justify-between border-b border-[#2D3442] px-6 py-4 sm:px-10">
        <Link to="/" data-testid="shared-home"><BrandLogo /></Link>
        <Link to={`/login?next=/read/${id}`} className="text-sm text-[#A8A090] hover:text-[#F3EBDD]" data-testid="shared-header-login">Log in</Link>
      </header>
      <section className="mx-auto flex max-w-4xl flex-col gap-8 px-6 py-12 sm:flex-row sm:px-10">
        <div className="flex aspect-[2/3] w-44 shrink-0 items-center justify-center overflow-hidden rounded bg-[#1A202C] shadow-[0_10px_40px_rgba(0,0,0,0.5)]">
          {s.has_cover ? <img src={`${API}/public/stories/${id}/cover?v=${s.cover_v || ""}`} alt="" className="h-full w-full object-cover" data-testid="shared-cover" /> : <BookOpen className="h-8 w-8 text-[#A8A090]" />}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">{s.universe_name}</div>
          <h1 className="mt-2 font-display text-4xl text-[#F3EBDD] sm:text-5xl [overflow-wrap:anywhere]" data-testid="shared-title">{s.title}</h1>
          {s.subtitle && <div className="mt-1 font-display text-xl italic text-[#D6CCBC]">{s.subtitle}</div>}
          {s.author && <div className="mt-1 text-sm text-[#A8A090]">by {s.author}</div>}
          <FanBadge story={s} className="mt-2" />
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#A8A090]">
            <span>{s.chapters} chapters · {fmtNum(s.words)} words</span>
            <span className="flex items-center gap-1 text-[#FBBF24]" data-testid="shared-rating"><Star className="h-3 w-3 fill-current" />{s.rating ? `${s.rating} (${s.ratings})` : "No ratings yet"}</span>
          </div>
          {s.synopsis && <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[#D6CCBC]" data-testid="shared-synopsis">{s.synopsis}</p>}
          <div className="mt-6"><Cta sid={id} id="top" /></div>
        </div>
      </section>
      {ch && (
        <main className="px-5 pb-16 sm:px-10">
          <article className="mx-auto max-w-2xl rounded-sm bg-[#F6F0E4] px-6 py-12 shadow-[0_10px_40px_rgba(0,0,0,0.5)] sm:px-14" data-testid="shared-chapter">
            <h2 className="mb-8 text-center font-display text-3xl text-[#231D15]">{chapterLabel(1, ch.title)}</h2>
            <ReaderText md={ch.content} />
          </article>
          {s.chapters > 1 && (
            <div className="mx-auto mt-10 max-w-2xl rounded-md border border-[#D97706]/50 bg-[#1A1508] p-6 text-center" data-testid="shared-continue">
              <div className="font-display text-2xl text-[#F3EBDD]">{s.chapters - 1} more chapter{s.chapters > 2 ? "s" : ""} to go</div>
              <p className="mb-4 mt-1 text-sm text-[#A8A090]">About {fmtMinutes(readMinutes(s.words, s.wpm))} of reading in total. Create a free LoreRight account to keep reading, rate the story and leave comments.</p>
              <div className="flex justify-center"><Cta sid={id} id="bottom" /></div>
            </div>
          )}
        </main>
      )}
      <footer className="flex justify-center pb-10"><RightsReportButton sid={id} title={s.title} /></footer>
    </div>
  );
}
